import { Paragraph, TextRun, convertMillimetersToTwip } from 'docx'
import {
  computeHiddenMarketsInfo,
  formatMarketLine
} from '../../modules/Questionnaire/MarketVisibility/useHiddenMarketsLabel'
import { MarketOption } from '../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { HIDDEN_MARKETS_PREFIX } from './model'

/**
 * "Hidden in: 🇩🇪 Germany, 🇫🇷 France" for an item's `hiddenInMarkets`, or `null`
 * when nothing applicable is hidden. Reuses the same pure logic
 * (`computeHiddenMarketsInfo` + `formatMarketLine`) as the on-screen indicators,
 * so the exported doc stays in lockstep with the builder UI.
 *
 * `parentHiddenInMarkets` narrows the applicable set for sub-items (a response
 * option / matrix row only applies where its parent question is visible), so an
 * option isn't reported hidden in a market where its question is already hidden.
 */
export const formatHiddenMarkets = (
  allMarkets: MarketOption[],
  hiddenInMarkets: string[] | null | undefined,
  parentHiddenInMarkets: string[] = []
): string | null => {
  const info = computeHiddenMarketsInfo(
    allMarkets,
    hiddenInMarkets ?? [],
    parentHiddenInMarkets
  )
  if (!info.isHidden) return null
  return `${HIDDEN_MARKETS_PREFIX} ${info.hiddenMarkets
    .map(formatMarketLine)
    .join(', ')}`
}

/**
 * A standalone, indented, muted line noting the markets a sub-item (response
 * option / matrix row / matrix column) is hidden in. Returns `null` when there
 * is nothing to show, so callers can spread it conditionally.
 *
 * Entry-level visibility is instead appended to the question header line (see
 * createQuestionTitle) — a separate line there would be misread as a response
 * option on re-import.
 */
export const createHiddenMarketsLine = (
  allMarkets: MarketOption[],
  hiddenInMarkets: string[] | null | undefined,
  parentHiddenInMarkets: string[] = [],
  indentMm = 12.6
): Paragraph | null => {
  const text = formatHiddenMarkets(
    allMarkets,
    hiddenInMarkets,
    parentHiddenInMarkets
  )
  if (!text) return null
  return new Paragraph({
    children: [new TextRun({ text, italics: true, color: '808080' })],
    indent: {
      start: convertMillimetersToTwip(indentMm)
    }
  })
}
